// components/WhyChoose.jsx
import React, { useEffect, useRef } from "react";
import "./WhyChoose.css";

const WhyChoose = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    // Observe the main section
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Observe all cards
    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const stats = [
    { number: "50+", label: "Projects Delivered", icon: "🚀" },
    { number: "24/7", label: "Client Support", icon: "💬" },
    { number: "100%", label: "Client Satisfaction", icon: "⭐" },
    { number: "5+", label: "Years Experience", icon: "📅" },
  ];

  const features = [
    {
      icon: "🎯",
      title: "ROI-Focused Approach",
      description:
        "Every project is built with measurable results in mind. We create digital assets that generate real value and business growth.",
    },
    {
      icon: "🚀",
      title: "Innovation First",
      description:
        "We stay ahead of trends, bringing cutting-edge technology and creative solutions to every project.",
    },
    {
      icon: "🤝",
      title: "Client Partnership",
      description:
        "We don't just work for you, we work with you. Your success is our success, and we're committed to long-term relationships.",
    },
    {
      icon: "⚡",
      title: "Fast Delivery",
      description:
        "Agile methodology ensures quick turnaround times without compromising on quality or performance.",
    },
    {
      icon: "🔒",
      title: "Security & Reliability",
      description:
        "Enterprise-grade security practices to keep your digital assets safe and your business running smoothly.",
    },
    {
      icon: "📊",
      title: "Data-Driven Decisions",
      description:
        "We use analytics and user data to guide our design and development decisions for optimal results.",
    },
  ];

  return (
    <section id="about" className="why-choose-section" ref={sectionRef}>
      {/* Animated Background Elements */}
      <div className="bg-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <span className="section-subtitle">Why Choose Us</span>
          <h2 className="section-title">
            We Don't Just Build Websites —
            <br />
            <span className="highlight">We Build Success Stories</span>
          </h2>
          <p className="section-description">
            With a passion for innovation and a commitment to excellence, we
            deliver digital solutions that drive real business results.
          </p>
        </div>

        {/* Stats Counter */}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="stat-card"
              ref={(el) => (cardsRef.current[index] = el)}
            >
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Vision & Mission Cards */}
        <div className="vision-mission-grid">
          <div className="vision-card" ref={(el) => (cardsRef.current[4] = el)}>
            <div className="card-icon">🔭</div>
            <h3 className="card-title">Our Vision</h3>
            <p className="card-text">
              To become a leading global tech solutions provider by crafting
              innovative, future-ready digital experiences that empower
              businesses to grow, compete, and thrive in the evolving digital
              landscape.
            </p>
            <div className="card-decoration"></div>
          </div>

          <div
            className="mission-card"
            ref={(el) => (cardsRef.current[5] = el)}
          >
            <div className="card-icon">🎯</div>
            <h3 className="card-title">Our Mission</h3>
            <p className="card-text">
              To deliver high-quality web development and creative digital
              services that blend design, technology, and strategy. We aim to
              support brands with solutions that are not only visually
              compelling but also technically strong and scalable.
            </p>
            <div className="card-decoration"></div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className="feature-card"
              ref={(el) => (cardsRef.current[6 + index] = el)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h4 className="feature-title">{feature.title}</h4>
              <p className="feature-description">{feature.description}</p>
              <div className="feature-hover-effect"></div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="cta-banner" ref={(el) => (cardsRef.current[12] = el)}>
          <div className="cta-content">
            <h3 className="cta-title">Ready to Start Your Journey?</h3>
            <p className="cta-text">
              Let's work together to bring your ideas to life and create
              something amazing.
            </p>
            <button className="cta-button">Get Started Today</button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
